import React, { useEffect, useState, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { useNavigateWithRange } from '../hooks/useNavigateWithRange';
import { useToolList } from '../services/toolsApi';
import ResizableTable from '../components/ResizableTable';
import { LOADING_MESSAGE } from '../utils/strings';
import { components, iconSize, iconShape, layout } from '../utils/designTokens';
import { formatDuration } from '../utils/formatters';

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent-amber/20 border border-accent-amber/50 rounded text-xs text-text-primary">
    {label}
    <button onClick={onRemove} className="hover:text-danger ml-1">×</button>
  </span>
);

const VirtualToolRow = React.memo(({ data, index, style }) => {
  const { data: tools, columnWidths, onOpen } = data;
  const tool = tools[index];

  const isRed = tool.error_rate > 10 || (tool.security_alerts || 0) > 0;
  const dotColor = isRed ? 'bg-danger' : 'bg-pastel-teal';

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '—';
    const now = Date.now();
    const then = new Date(timestamp).getTime();
    const diff = now - then;
    const minutes = Math.floor(Math.abs(diff) / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const last = formatTimeAgo(tool.last_seen);

  const agentsDisplay = tool.agents && tool.agents.length > 0
    ? tool.agents.join(', ')
    : '—';

  return (
    <div
      style={style}
      onClick={() => onOpen(tool.name)}
      className={`${components.table.row.container} ${components.table.row.border} ${components.table.row.hover} cursor-pointer`}
    >
      {/* Column 1: Tool Name */}
      <div
        className={`flex items-center ${components.table.cell.gap} ${components.table.cell.padding}`}
        style={{ width: `${columnWidths.tool}px`, minWidth: `${columnWidths.tool}px`, flex: 'none' }}
      >
        <div className={`${iconSize.dot} ${iconShape.dot} flex-shrink-0 ${dotColor}`}></div>
        <span className={`${components.table.cell.text.id} truncate`} style={{ color: components.table.cell.colors.entity }}>{tool.name}</span>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />

      {/* Column 2: Calls */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.calls}px`, minWidth: `${columnWidths.calls}px`, flex: 'none', color: components.table.cell.colors.metric }}
        title="Tool calls in window"
      >
        {tool.calls || 0}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />

      {/* Column 3: Total Duration */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.duration}px`, minWidth: `${columnWidths.duration}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatDuration(tool.total_duration)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />

      {/* Column 4: Error Rate */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.errorRate}px`, minWidth: `${columnWidths.errorRate}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {(tool.error_rate || 0).toFixed(1)}%
      </div>
      <div style={{ width: '3px', flex: 'none' }} />

      {/* Column 5: Agents */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.agents}px`, minWidth: `${columnWidths.agents}px`, flex: 'none', color: components.table.cell.colors.metric }}
        title={agentsDisplay}
      >
        <div className="truncate">{agentsDisplay}</div>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />

      {/* Column 6: Last Activity */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted}`}
        style={{ width: `${columnWidths.lastActivity}px`, minWidth: `${columnWidths.lastActivity}px`, flex: 'none', color: components.table.cell.colors.time }}
      >
        {last}
      </div>
    </div>
  );
});

export default function Tools({ onTabChange, onKpisReady }) {
  const { selectedAppId } = useParams();
  const navigate = useNavigateWithRange();

  const [filters, setFilters] = useState({});
  const [query, setQuery] = useState('');

  const { tools: toolsData, loading, error } = useToolList(selectedAppId);

  // Calculate global KPIs from tools data
  const globalKPIs = useMemo(() => {
    if (!toolsData.length) return null;

    const totalTools = toolsData.length;

    const totalCalls = toolsData.reduce((sum, tool) => sum + (tool.calls || 0), 0);
    const totalErrors = toolsData.reduce((sum, tool) => {
      const errorCount = Math.round((tool.error_rate || 0) / 100 * (tool.calls || 0));
      return sum + errorCount;
    }, 0);
    const errorRate = totalCalls > 0 ? (totalErrors / totalCalls) * 100 : 0;

    const securityAlerts = toolsData.reduce((sum, tool) => sum + (tool.security_alerts || 0), 0);

    return {
      totalTools,
      errorRate,
      securityAlerts
    };
  }, [toolsData]);

  const applyFilters = (list) => {
    let out = [...list];
    if (filters.hasErrors) out = out.filter(t => (t.error_rate || 0) > 0);
    if (filters.hasSecurityHigh) out = out.filter(t => (t.security_alerts || 0) > 0);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(t => (t.name || '').toLowerCase().includes(q));
    }
    return out;
  };

  const filteredTools = applyFilters(toolsData);
  // Tools lives only under Journeys, so tag the Traces breadcrumb back to it.
  const openTool = (toolName) => navigate(`/app/${selectedAppId}/traces`, { tool: toolName, from: 'journeys-tools' });

  // Pass KPIs to parent component
  useEffect(() => {
    if (onKpisReady && globalKPIs) {
      onKpisReady({
        kpis: globalKPIs,
        filters: filters,
        setFilters: setFilters
      });
    }
  }, [globalKPIs, filters]); // Removed onKpisReady - setState functions are stable

  if (loading) {
    return <div className={components.table.empty}>{LOADING_MESSAGE}</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-text-primary mb-2">Error Loading Tools</h3>
          <p className="text-danger">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {filteredTools.length === 0 ? (
        <div className={components.table.empty}>
          {toolsData.length === 0
            ? "No tools found. Tools will appear here when your service generates tool telemetry data."
            : "No tools match."}
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <ResizableTable
            columns={[
              { key: 'tool', label: 'Tool', defaultWidth: 200, minWidth: 120 },
              { key: 'calls', label: 'Calls', defaultWidth: 90, minWidth: 60 },
              { key: 'duration', label: 'Total Duration', defaultWidth: 130, minWidth: 90 },
              { key: 'errorRate', label: 'Error Rate', defaultWidth: 110, minWidth: 80 },
              { key: 'agents', label: 'Agents', defaultWidth: 250, minWidth: 150 },
              { key: 'lastActivity', label: 'Last Activity', defaultWidth: 130, minWidth: 110 },
            ]}
            data={filteredTools}
            renderRow={VirtualToolRow}
            onOpen={openTool}
            storageKey="lumoz-tools-table-widths"
            rowHeight={layout.rowHeight}
            containerHeight="calc(100vh - 350px)"
          />
        </div>
      )}
    </div>
  );
}